import { addDays, endOfDay, startOfDay } from 'date-fns';
import { useGoals } from 'features/goals';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useToday } from 'shared/contexts/today';
import { useUser } from 'shared/hooks/use-user';
import {
  DateSectionOptions,
  overdueSectionID,
} from 'shared/types/orderable-section';
import { SortingMode } from 'shared/types/sorting-mode';
import { TaskType } from 'shared/types/task-base';
import { TaskCardTask } from 'shared/types/task-card-task';
import { formatDateKey } from 'shared/utils/format-date-key';
import { getCustomOrderTillDate } from 'shared/utils/get-custom-order-till-date';
import { getSortedTasks } from 'shared/utils/get-sorted-tasks';
import { getTimeFormat } from 'shared/utils/get-time-format';
import { getWeekStartsOn } from 'shared/utils/get-week-starts-on';
import { mapTaskToTaskCard } from 'shared/utils/mappers/map-task-to-task-card';

import { useDueStatusGroupedTasks } from './use-due-status-grouped-tasks';
import { useTodayTasks } from './use-today-tasks';

export const useNoGroupingTaskSections = () => {
  const { t } = useTranslation();
  const today = useToday();

  const tasks = useTodayTasks();
  const goals = useGoals();
  const user = useUser();
  const weekStartsOn = getWeekStartsOn(user);
  const timeFormat = getTimeFormat(user);

  const { todayTasks, overdueTasks } = useDueStatusGroupedTasks(tasks);

  const sections = useMemo(() => {
    const startDateOverdue = new Date(0);
    const endDateOverdue = endOfDay(addDays(today, -1));
    const startToday = startOfDay(today);
    const endToday = endOfDay(today);

    return [
      // overdue
      {
        id: overdueSectionID,
        type: DateSectionOptions.Day,
        startDate: startDateOverdue,
        endDate: endDateOverdue,
        deadline: endDateOverdue,
        items: overdueTasks.length
          ? getSortedTasks(overdueTasks, {
              today,
              weekStartsOn,
              mode: [SortingMode.DueDate],
              // overdue tasks need the timeslots to be inverted
              invertedTimeslots: true,
              allDayFirst: true,
            }).map((task) =>
              mapTaskToTaskCard(task, {
                today,
                goals,
                t,
                showDue: true,
                hideTodayDue: true,
                hideFutureDue: task.type !== TaskType.Task,
                weekStartsOn,
                timeFormat,
              }),
            )
          : [],
      },
      // today
      {
        id: formatDateKey(endToday),
        type: DateSectionOptions.Day,
        startDate: startToday,
        endDate: endToday,
        deadline: endToday,
        items: getSortedTasks(todayTasks, {
          today,
          weekStartsOn,
          mode: Array.from(
            new Set([SortingMode.DueDate, user?.settings?.todoSortingMode]),
          ).filter(Boolean) as SortingMode[],
          order: getCustomOrderTillDate(
            user?.settings?.todoSorting ?? {},
            endToday,
          ),
        }).map((task) =>
          mapTaskToTaskCard(task, {
            today,
            goals,
            t,
            showDue: true,
            hideTodayDue: true,
            hideFutureDue: task.type !== TaskType.Task,
            weekStartsOn,
            timeFormat,
          }),
        ),
      },
    ].filter(({ id, items }) => items.length || id !== overdueSectionID);
  }, [
    goals,
    overdueTasks,
    t,
    timeFormat,
    today,
    todayTasks,
    user?.settings?.todoSorting,
    user?.settings?.todoSortingMode,
    weekStartsOn,
  ]);

  const taskCardTasks = useMemo(
    () =>
      sections.reduce<TaskCardTask[]>((acc, { items }) => {
        acc.push(...items);
        return acc;
      }, []),
    [sections],
  );

  return {
    sections,
    taskCardTasks,
  };
};
